import {
    AUTH_PROVIDERS, AuthProvider, AuthSecrets, AuthSettings, KeycloakSettings,
    LdapSettings, PROVISION_ROLES, ProvisionRole, TestResult,
} from "./generalType";

// Pure functions only: parsing what the hub sends, deciding what the page
// shows, building what goes back. The thunks and the view stay thin around
// these. Every function is total - malformed input gives a value, not an
// exception.

export const DEFAULT_PROVIDERS: readonly AuthProvider[] = ["local"];

export const EMPTY_LDAP: LdapSettings = {
    server: "",
    port: 389,
    baseDn: "",
    bindDn: "",
    bindPasswordSet: false,
    userAttribute: "",
    group: "",
    autoProvision: false,
};

export const EMPTY_KEYCLOAK: KeycloakSettings = {
    url: "",
    realm: "",
    clientId: "",
    clientSecretSet: false,
    role: "",
    autoProvision: false,
    redirectUri: "",
    scope: "openid profile email",
};

export const EMPTY_SETTINGS: AuthSettings = {
    providers: DEFAULT_PROVIDERS,
    provisionRole: "Guest",
    ldap: EMPTY_LDAP,
    keycloak: EMPTY_KEYCLOAK,
};

export const NO_SECRETS: AuthSecrets = { bindPassword: "", clientSecret: "" };

const isAuthProvider = (value: string): value is AuthProvider =>
    (AUTH_PROVIDERS as readonly string[]).includes(value);

/**
 * "local, LDAP,foo,local" -> ["local", "ldap"]. Unknown entries and
 * duplicates are dropped, case and blanks are ignored. Nothing usable left
 * means the hub's default, local login.
 */
export function parseProviders(raw: unknown): readonly AuthProvider[] {
    const parsed = (typeof raw === "string" ? raw : "")
        .split(",")
        .map((entry) => entry.trim().toLowerCase())
        .filter(isAuthProvider)
        .filter((entry, index, all) => all.indexOf(entry) === index);

    return parsed.length > 0 ? parsed : DEFAULT_PROVIDERS;
}

export const formatProviders = (providers: readonly AuthProvider[]): string =>
    providers.join(",");

/** Anything but Guest/User/Admin (a machine role, a typo) becomes Guest. */
export function parseProvisionRole(raw: unknown): ProvisionRole {
    const match = PROVISION_ROLES.find(
        (role) => typeof raw === "string" && role.toLowerCase() === raw.trim().toLowerCase());
    return match ?? "Guest";
}

const asString = (value: unknown, fallback: string): string =>
    typeof value === "string" ? value : fallback;

const asBoolean = (value: unknown, fallback: boolean): boolean =>
    typeof value === "boolean" ? value : fallback;

const asPort = (value: unknown, fallback: number | null): number | null =>
    typeof value === "number" && Number.isInteger(value) && value > 0 && value < 65536
        ? value
        : value === null ? null : fallback;

const asRecord = (value: unknown): Record<string, unknown> =>
    typeof value === "object" && value !== null ? value as Record<string, unknown> : {};

export function parseLdap(raw: unknown): LdapSettings {
    const r = asRecord(raw);
    return {
        server: asString(r.server, EMPTY_LDAP.server),
        port: asPort(r.port, EMPTY_LDAP.port),
        baseDn: asString(r.baseDn, EMPTY_LDAP.baseDn),
        bindDn: asString(r.bindDn, EMPTY_LDAP.bindDn),
        bindPasswordSet: asBoolean(r.bindPasswordSet, EMPTY_LDAP.bindPasswordSet),
        userAttribute: asString(r.userAttribute, EMPTY_LDAP.userAttribute),
        group: asString(r.group, EMPTY_LDAP.group),
        autoProvision: asBoolean(r.autoProvision, EMPTY_LDAP.autoProvision),
    };
}

export function parseKeycloak(raw: unknown): KeycloakSettings {
    const r = asRecord(raw);
    return {
        url: asString(r.url, EMPTY_KEYCLOAK.url),
        realm: asString(r.realm, EMPTY_KEYCLOAK.realm),
        clientId: asString(r.clientId, EMPTY_KEYCLOAK.clientId),
        clientSecretSet: asBoolean(r.clientSecretSet, EMPTY_KEYCLOAK.clientSecretSet),
        role: asString(r.role, EMPTY_KEYCLOAK.role),
        autoProvision: asBoolean(r.autoProvision, EMPTY_KEYCLOAK.autoProvision),
        redirectUri: asString(r.redirectUri, EMPTY_KEYCLOAK.redirectUri),
        scope: asString(r.scope, EMPTY_KEYCLOAK.scope),
    };
}

/** The answer of GET /AuthSettings, turned into something that cannot be wrong. */
export function parseAuthSettings(raw: unknown): AuthSettings {
    const r = asRecord(raw);
    return {
        providers: parseProviders(r.authProvider),
        provisionRole: parseProvisionRole(r.provisionRole),
        ldap: parseLdap(r.ldap),
        keycloak: parseKeycloak(r.keycloak),
    };
}

/**
 * The body of PUT /AuthSettings. An empty secret goes out as null, which the
 * hub reads as "keep the stored one".
 */
export function toPutBody(settings: AuthSettings, secrets: AuthSecrets) {
    const secretOrNull = (value: string): string | null => value === "" ? null : value;
    return {
        authProvider: formatProviders(settings.providers),
        provisionRole: settings.provisionRole,
        ldap: { ...settings.ldap, bindPassword: secretOrNull(secrets.bindPassword) },
        keycloak: { ...settings.keycloak, clientSecret: secretOrNull(secrets.clientSecret) },
    };
}

/**
 * Error messages from a 400 answer. The contract promises
 * `{ "errors": ["..."] }`; ASP.NET's own validation answer has
 * `{ "errors": { "Field": ["..."] } }`, so that is read as well.
 */
export function parseErrors(raw: unknown): readonly string[] {
    const errors = asRecord(raw).errors;
    const flat: unknown[] = Array.isArray(errors)
        ? errors
        : Object.values(asRecord(errors)).flatMap((v) => Array.isArray(v) ? v : [v]);
    return flat.filter((e): e is string => typeof e === "string" && e.trim() !== "");
}

export function parseTestResult(raw: unknown): TestResult {
    const r = asRecord(raw);
    return {
        kind: "done",
        ok: r.ok === true,
        message: asString(r.message, ""),
    };
}

// --- The login method list --------------------------------------------------

/** Adds a method at the end or removes it. The list never ends up empty. */
export function toggleProvider(
    providers: readonly AuthProvider[], provider: AuthProvider, enabled: boolean,
): readonly AuthProvider[] {
    const without = providers.filter((p) => p !== provider);
    if (enabled) return [...without, provider];
    return without.length > 0 ? without : providers;
}

/** Moves the entry at `index` by `delta` places; out of range changes nothing. */
export function moveProvider(
    providers: readonly AuthProvider[], index: number, delta: -1 | 1,
): readonly AuthProvider[] {
    const target = index + delta;
    if (index < 0 || index >= providers.length || target < 0 || target >= providers.length) {
        return providers;
    }
    return providers.map((p, i) =>
        i === index ? providers[target] : i === target ? providers[index] : p);
}

/** What the page says above the list. */
export type ProviderVerdict =
    | { readonly kind: "localOnly" }
    | { readonly kind: "mixed" }
    | { readonly kind: "noLocal" };

export function judgeProviders(providers: readonly AuthProvider[]): ProviderVerdict {
    if (providers.length === 1 && providers[0] === "local") return { kind: "localOnly" };
    return providers.includes("local") ? { kind: "mixed" } : { kind: "noLocal" };
}

/** LDAP and Active Directory share one set of settings. */
export const isLdapActive = (providers: readonly AuthProvider[]): boolean =>
    providers.includes("ldap") || providers.includes("activedirectory");

export const isKeycloakActive = (providers: readonly AuthProvider[]): boolean =>
    providers.includes("keycloak");
